// Lifecycle of a 1:1 call answered from the global incoming-call banner (calling reliability F-08).
//
// The banner (IncomingCallContext) answers on the control plane and the call then runs in the
// standalone /connect/:meetingId room — usually another tab. That room is a generic meeting UI, so
// this module gives the call the same instance-aware lifecycle the Chat module has:
//   • call_heartbeat (v8 §7.1 L2) for the answered call_instance_id while the room is mounted;
//   • leaving the room is the callee's hang-up: an instance-bound call_end (never end-for-all, so
//     only that exact call can end — a newer call is never affected);
//   • the call ending elsewhere (caller hang-up, server end, answered on another device) leaves the
//     room LOCALLY, with no call_end and no end-for-all (F-06).
//
// Per tab, keyed by call_instance_id. Releasing defers the hang-up briefly so a React StrictMode /
// fast remount re-acquires instead of hanging up. Without a call_instance_id (legacy backend)
// everything here is a no-op, so CALL_INSTANCE_V1=false behaviour is unchanged.

import { workspaceSocket } from "../../../utils/workspaceSocket";
import { HEARTBEAT_INTERVAL_MS, TERMINAL_STATES, heartbeatPayload, queueCallSignal, toDeadline } from "./callIdentity";

export const BANNER_HANGUP_DEFER_MS = 300;
const CHANNEL_NAME = "synex_banner_call_lifecycle";
const TERMINATED_PREFIX = "synex_banner_call_terminated:";
const TERMINATED_TTL_MS = 5 * 60 * 1000;
const NOOP_HANDLE = { release() {}, continueElsewhere() {} };

// ── Termination notices: same tab (listeners), other tabs (BroadcastChannel), late mounts (storage) ──
const listeners = new Set();
let channel;

function deliver(msg) {
  listeners.forEach((fn) => {
    try {
      fn(msg);
    } catch (_) {
      /* a listener error never blocks the others */
    }
  });
}

function ensureChannel() {
  if (channel !== undefined) return channel;
  channel = null;
  try {
    if (typeof window !== "undefined" && typeof window.BroadcastChannel === "function") {
      channel = new window.BroadcastChannel(CHANNEL_NAME);
      channel.onmessage = (e) => deliver(e && e.data);
    }
  } catch (_) {
    channel = null;
  }
  return channel;
}

function markTerminated(ci, reason) {
  try {
    localStorage.setItem(TERMINATED_PREFIX + ci, JSON.stringify({ reason, at: Date.now() }));
  } catch (_) {
    /* storage unavailable: the live notice still applies */
  }
}

function readTerminated(ci) {
  try {
    const raw = localStorage.getItem(TERMINATED_PREFIX + ci);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || Date.now() - Number(parsed.at || 0) > TERMINATED_TTL_MS) {
      localStorage.removeItem(TERMINATED_PREFIX + ci);
      return null;
    }
    return parsed.reason || "ended";
  } catch (_) {
    return null;
  }
}

/**
 * The banner tab learned that the call it answered is over for this device (answered on another
 * device: the backend replies only to the answering connection). Tells the room in any tab.
 */
export function notifyBannerCallTerminated(callInstanceId, reason) {
  if (!callInstanceId) return;
  const msg = { type: "banner_call_terminated", call_instance_id: String(callInstanceId), reason: reason || "ended" };
  markTerminated(msg.call_instance_id, msg.reason);
  deliver(msg);
  try {
    const ch = ensureChannel();
    if (ch) ch.postMessage(msg);
  } catch (_) {
    /* other tabs still see the storage marker when they mount */
  }
}

// ── Per-call sessions ───────────────────────────────────────────────────────────────────────
const sessions = new Map();

function defaultSend(payload) {
  return workspaceSocket.send(payload);
}

function stopTimers(s) {
  if (s.heartbeatTimer) clearInterval(s.heartbeatTimer);
  s.heartbeatTimer = null;
}

function terminate(s, reason) {
  if (s.ended || s.done) return;
  s.ended = true;
  s.noHangup = true; // over elsewhere: this room never signals call_end
  stopTimers(s);
  const h = s.handlers || {};
  try {
    if (reason === "answered_elsewhere") h.onAnsweredElsewhere?.();
    else h.onEndedRemotely?.();
  } catch (_) {
    /* UI callback failure must not break the lifecycle */
  }
}

function finish(s, hangup) {
  if (s.done) return;
  s.done = true;
  stopTimers(s);
  if (s.unsubSocket) s.unsubSocket();
  listeners.delete(s.onNotice);
  sessions.delete(s.ci);
  if (!hangup || s.noHangup || s.ended) return;
  const d = s.descriptor;
  const payload = {
    type: "call_end",
    chat_id: d.chat_id,
    ...(d.peer_user_id ? { to_user_id: d.peer_user_id } : {}),
    call_instance_id: s.ci,
    ...(toDeadline(d.ring_deadline_at) ? { ring_deadline_at: toDeadline(d.ring_deadline_at) } : {}),
  };
  let sent = false;
  try {
    sent = s.send(payload) !== false;
  } catch (_) {
    sent = false;
  }
  if (!sent) queueCallSignal(payload); // delivered on reconnect; bound to this instance only
}

function createSession(descriptor, send) {
  const s = {
    ci: String(descriptor.call_instance_id),
    descriptor,
    send: send || defaultSend,
    refs: 0,
    done: false,
    ended: false,
    noHangup: false,
    handlers: null,
    hangupTimer: null,
    heartbeatTimer: null,
    unsubSocket: null,
  };
  const beat = () => {
    if (s.ended || s.done) return;
    let livekitState;
    try {
      livekitState = s.handlers?.getLiveKitState?.();
    } catch (_) {
      livekitState = undefined;
    }
    const payload = heartbeatPayload({ callInstanceId: s.ci }, "connected", livekitState);
    try {
      if (payload) s.send(payload);
    } catch (_) {
      /* retried on the next tick */
    }
  };
  s.onSocket = (data) => {
    if (!data || !data.call_instance_id || String(data.call_instance_id) !== s.ci) return;
    const ev = data.event || data.type;
    if (ev === "call_end" || ev === "call_reject" || ev === "call_busy") {
      terminate(s, String(data.reason || "") === "answered_elsewhere" ? "answered_elsewhere" : "ended");
    } else if (ev === "call_state_sync" && TERMINAL_STATES.includes(String(data.state))) {
      terminate(s, "ended");
    }
  };
  s.onNotice = (msg) => {
    if (msg && msg.type === "banner_call_terminated" && msg.call_instance_id === s.ci) terminate(s, msg.reason);
  };
  ensureChannel();
  listeners.add(s.onNotice);
  s.unsubSocket = workspaceSocket.subscribe(s.onSocket);
  beat();
  s.heartbeatTimer = setInterval(beat, HEARTBEAT_INTERVAL_MS);
  return s;
}

/**
 * Bind the standalone room to the banner-answered call described by `descriptor`
 * ({ call_instance_id, chat_id, peer_user_id, ring_deadline_at, meeting_id }).
 * handlers: { getLiveKitState, onEndedRemotely, onAnsweredElsewhere }.
 * Returns { release() — the room was left (hang up after a short defer),
 *           continueElsewhere() — the call continues in another tab/device: never hang up }.
 */
export function acquireBannerCall(descriptor, handlers = {}, { send } = {}) {
  if (!descriptor || !descriptor.call_instance_id || !descriptor.chat_id) return NOOP_HANDLE;
  const ci = String(descriptor.call_instance_id);
  let s = sessions.get(ci);
  if (!s) {
    s = createSession(descriptor, send);
    sessions.set(ci, s);
  }
  s.refs += 1;
  if (s.hangupTimer) {
    clearTimeout(s.hangupTimer);
    s.hangupTimer = null;
  }
  s.handlers = handlers;
  const prior = readTerminated(ci);
  if (prior) setTimeout(() => terminate(s, prior), 0); // the notice arrived before this room mounted
  let released = false;
  return {
    release() {
      if (released) return;
      released = true;
      s.refs -= 1;
      if (s.refs > 0) return;
      s.hangupTimer = setTimeout(() => {
        s.hangupTimer = null;
        if (s.refs <= 0) finish(s, true);
      }, BANNER_HANGUP_DEFER_MS);
    },
    continueElsewhere() {
      s.noHangup = true;
    },
  };
}

/** Test hook: forget every session and notice. */
export function __resetBannerCallsForTests() {
  sessions.forEach((s) => {
    if (s.hangupTimer) clearTimeout(s.hangupTimer);
    s.noHangup = true;
    finish(s, false);
  });
  sessions.clear();
  listeners.clear();
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith(TERMINATED_PREFIX))
      .forEach((k) => localStorage.removeItem(k));
  } catch (_) {
    /* ignore */
  }
}
